import { ChevronDown } from 'lucide-react';

import {
  BENCHMARK_PERCENT,
  BENCHMARK_RUN_DATE,
  BENCHMARK_SITES,
  BENCHMARK_TOTAL,
} from '../../content/benchmarks';
import { coverage } from '../../content/checks';
import { Container, formatDate } from '../ui';

/** The proof band: one number, two supporting ones, and the method on
 *  request. */
export function Benchmarks() {
  return (
    <section
      aria-labelledby="bench-title"
      className="bg-ink py-24 text-white md:py-32"
    >
      <Container>
        <h2 className="sr-only" id="bench-title">
          Measured results
        </h2>
        <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="font-report text-hero font-medium tabular-nums text-white">
              {BENCHMARK_PERCENT}%
            </p>
            <p className="mt-4 max-w-md text-report-lede text-white/80">
              {BENCHMARK_TOTAL.found} of {BENCHMARK_TOTAL.total} known barriers
              found by our first pass, before specialist review.
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-8 lg:col-span-5">
            <div className="flex flex-col-reverse">
              <dt className="mt-2 text-ui text-white/70">
                WCAG 2.2 A/AA criteria tested by tooling
              </dt>
              <dd className="font-report text-report-stat font-medium tabular-nums">
                {coverage.scsCovered}/{coverage.scsTotal}
              </dd>
            </div>
            <div className="flex flex-col-reverse">
              <dt className="mt-2 text-ui text-white/70">
                of findings verified by a specialist
              </dt>
              <dd className="font-report text-report-stat font-medium tabular-nums">
                100%
              </dd>
            </div>
          </dl>
        </div>

        <details className="group mt-14 border-t border-white/15 pt-6">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-button text-ui-strong font-semibold text-white hover:underline [&::-webkit-details-marker]:hidden">
            How we measured
            <ChevronDown
              aria-hidden
              className="transition-transform group-open:rotate-180"
              size={16}
            />
          </summary>
          <div className="mt-6 grid gap-8 lg:grid-cols-12">
            <p className="text-ui-lg text-white/80 lg:col-span-5">
              Universities and governments publish deliberately inaccessible
              demo sites with an answer key of every planted barrier. We ran our
              tooling against them on {formatDate(BENCHMARK_RUN_DATE)}. Counts
              are WCAG A/AA barriers detectable on a single page. Several of
              these sites were used while developing our checks. These numbers
              are tooling only; in every audit a specialist verifies each
              finding and assesses what automation can&rsquo;t.
            </p>
            <div className="overflow-x-auto rounded-card border border-white/15 lg:col-span-7">
              <table className="w-full text-left text-ui">
                <caption className="sr-only">
                  Documented barriers found by the automated pass, per test site
                </caption>
                <thead>
                  <tr className="border-b border-white/15 text-small-label uppercase text-white/70">
                    <th className="px-5 py-3 font-semibold" scope="col">
                      Test site
                    </th>
                    <th
                      className="px-5 py-3 text-right font-semibold"
                      scope="col"
                    >
                      Found
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {BENCHMARK_SITES.map((site) => (
                    <tr
                      className="border-b border-white/10 last:border-0"
                      key={site.name}
                    >
                      <th className="px-5 py-3 font-normal" scope="row">
                        {site.url ? (
                          <a
                            className="font-medium text-white underline-offset-4 hover:underline"
                            href={site.url}
                          >
                            {site.name}
                          </a>
                        ) : (
                          <span className="font-medium text-white">
                            {site.name}
                          </span>
                        )}
                        <span className="block text-caption text-white/70">
                          {site.publisher}
                        </span>
                      </th>
                      <td className="px-5 py-3 text-right tabular-nums text-white">
                        {site.found} / {site.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      </Container>
    </section>
  );
}
